import type { TokenPair } from '@/api/pending'

const REFRESH_KEY = 'condi.refresh_token'

type Listener = () => void

/**
 * Session tokens. The access token lives in memory only; the refresh token in localStorage
 * (acceptable because web sessions are revocable server-side, CSP is strict, and no
 * third-party scripts run). Other tabs are kept in sync through the `storage` event.
 */
function createSessionStore(storage: Storage | null) {
  let accessToken: string | null = null
  const listeners = new Set<Listener>()
  const notify = () => listeners.forEach((l) => l())

  const readRefresh = () => storage?.getItem(REFRESH_KEY) ?? null

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key !== REFRESH_KEY && e.key !== null) return
      // Another tab logged in (new token: our access token is stale or missing) or out (gone).
      if (!readRefresh()) accessToken = null
      notify()
    })
  }

  return {
    getAccessToken: () => accessToken,
    getRefreshToken: readRefresh,
    hasSession: () => accessToken !== null || readRefresh() !== null,
    setTokens(tokens: TokenPair) {
      accessToken = tokens.access_token
      storage?.setItem(REFRESH_KEY, tokens.refresh_token)
      notify()
    },
    clear() {
      accessToken = null
      storage?.removeItem(REFRESH_KEY)
      notify()
    },
    subscribe(listener: Listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

export type SessionStore = ReturnType<typeof createSessionStore>

export const sessionStore = createSessionStore(
  typeof localStorage === 'undefined' ? null : localStorage,
)

export { createSessionStore }
